import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import {
  validateMnemonic,
  mnemonicToPrivateKey,
  getAddressFromPrivateKey,
  signMessage,
  privateKeyToWIF,
  wifToPrivateKey,
} from '../lib/wallet'
import { setAuthCredentials, clearAuthCredentials } from '../lib/api'

const STORAGE_KEY = 'dogici_admin_session'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  address: string | null
  login: (mnemonic: string) => Promise<string>
  logout: () => void
  sign: (message: string) => Promise<string>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null)
  const [privateKey, setPrivateKey] = useState<Uint8Array | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const { wif } = JSON.parse(stored)
        const key = wifToPrivateKey(wif)
        const addr = getAddressFromPrivateKey(key)

        setPrivateKey(key)
        setAddress(addr)
        setAuthCredentials(key, addr)
      }
    } catch (err) {
      // Invalid stored data, clear it
      localStorage.removeItem(STORAGE_KEY)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const login = useCallback(async (mnemonic: string): Promise<string> => {
    if (!validateMnemonic(mnemonic)) {
      throw new Error('Invalid seed phrase')
    }

    const key = mnemonicToPrivateKey(mnemonic)
    const addr = getAddressFromPrivateKey(key)

    // Store WIF in localStorage for session persistence
    const wif = privateKeyToWIF(key)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ wif }))

    setPrivateKey(key)
    setAddress(addr)
    setAuthCredentials(key, addr)

    return addr
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setPrivateKey(null)
    setAddress(null)
    clearAuthCredentials()
  }, [])

  const sign = useCallback(
    async (message: string): Promise<string> => {
      if (!privateKey) {
        throw new Error('Not authenticated')
      }
      return signMessage(privateKey, message)
    },
    [privateKey]
  )

  const value: AuthContextType = {
    isAuthenticated: !!address,
    isLoading,
    address,
    login,
    logout,
    sign,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
